import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import { Audio } from 'react-loader-spinner';
import { MdOutlineCancel } from 'react-icons/md';
import { IoMdAdd } from 'react-icons/io';
import { Layout, Header } from '../../../components';
import { handleApi, formatMediumDate, formatCurrency } from '../../../utils/utils';
import AppContext from '../../../contexts/AppContext';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const { userData, currentColor ,setSuppressAutoNavigate} = useContext(AppContext); 
  const [selectedRowId, setSelectedRowId] = useState(null);
  const navigate = useNavigate();
  const itemsPerPage = 10;
  const dropdownRef = useRef(null);
  
  // Fetch employees on initial load and when the search term changes
  useEffect(() => {
    const timeout = setTimeout(() => fetchEmployees(), 500);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // Reset page to 1 when search term changes
  useEffect(() => setCurrentPage(1), [searchTerm]);

  const fetchEmployees = async () => {
      const apiEndpoint = 'Employees';
      setIsLoading(true);
      try {
        const response = await handleApi(apiEndpoint, {}, userData, 'GET', {});
        if (response.error) {
          toast.error(`Error: ${response.errorMessage}`);
          return;
        }
        console.log('response data sir',response.data)
        setEmployees(response.data);
  
      } catch (error) {
        toast.error(error.message || 'An unexpected error occurred.');
      } finally {setIsLoading(false);}
    };
  
  // Memoized and debounced search handler to avoid excessive re-renders
  const handleSearchChange = (e) => setSearchTerm(e.target.value);
  const debouncedSearchTerm = useMemo(() => debounce(handleSearchChange, 300), []);
  
  // Memoized filtering and sorting to improve performance
  const filteredEmployees = useMemo(() => {
    return employees.filter((emp) =>
      emp?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
      emp?.first_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [employees, searchTerm]);
  
  const sortedFilteredEmployees = filteredEmployees.sort(
    (a, b) => new Date(b.create_time) - new Date(a.create_time)
  );
  
  const indexOfLastItem = currentPage * itemsPerPage;
  const currentItems = sortedFilteredEmployees.slice(indexOfLastItem - itemsPerPage, indexOfLastItem);
  const totalPages = Math.ceil(filteredEmployees.length / itemsPerPage);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  const handleClosePage = () => {
    navigate(-1); 
  };
    
  const handleAction = (action, employee) => {
    setSuppressAutoNavigate(true);
    console.log('employee sir',employee);
    switch (action) {
      case 'new':
        navigate(`/employees/new`, { state: { employee: employee } });
        break;
      case 'view':
        navigate(`/employees/view/${employee.id}`, { state: { initialEmployee: employee } });
        break;
      case 'edit':
        navigate(`/employees/edit/${employee.id}`, { state: { initialEmployee: employee } });
        break;
      case 'deactivate':
        navigate(`/employees/deactivate/${employee.id}`, { state: { initialEmployee: employee } });
        break;
      default:
        toast.error(`Action "${action}" is not supported.`);
    }
  };

  return (
    <Layout>
      {/* Main container with a modern, light background */}
      <div className="min-h-screen bg-white text-white  p-4 sm:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Header and Close Button */}
          <div className="relative flex justify-between items-center">
            <h1 className="text-3xl font-extrabold text-gray-800 tracking-wide flex-grow text-center">
              EMPLOYEES
            </h1>
            <button
              onClick={handleClosePage}
              className="p-2 rounded-full text-gray-800 bg-gray-200 hover:bg-gray-300 transition-all duration-300 "
              style={{ color: currentColor }}
            >
              <MdOutlineCancel size={30} />
            </button>
          </div>
          <ToastContainer position="top-center" autoClose={2000} />
          
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20">
              <Audio height="80" width="80" radius="9" color={currentColor} ariaLabel="loading" />
              <p className="mt-4 text-gray-500 text-lg animate-pulse">Loading employees...</p>
            </div>
          ) : (
            <div className="bg-gray-400 p-4 sm:p-6  rounded-3xl  border border-gray-300 mx-auto max-w-[60%]">
              {/* Search and New Employee Section */}
              <div className="mb-2 flex flex-col sm:flex-row justify-between items-center gap-4">
                {/* Search Input */}
                <div className="relative w-full sm:w-1/2">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-xl text-gray-400 select-none">
                    🔍
                  </span>
                  <input
                    type="text"
                    placeholder="Search by name..."
                    onChange={debouncedSearchTerm}
                    className="w-full py-3 pl-12 pr-6 rounded-full bg-gray-200 text-gray-800 placeholder-gray-500  focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 focus:ring-offset-white transition duration-300 text-base"
                  />
                </div>
                {/* Add New Button */}
                <button
                  className="w-full sm:w-auto text-white px-6 py-3 font-semibold rounded-full flex items-center justify-center gap-2 
                             bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 
                             transition-all duration-300  hover:shadow-xl transform hover:-translate-y-0.5"
                  onClick={() => handleAction('new', {})}
                >
                  <IoMdAdd size={24} />
                  <span>New</span>
                </button>
              </div>

              {/* Pagination */}
              <div className="flex flex-wrap justify-center gap-2 mb-2 ">
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => paginate(index + 1)}
                    className={`px-4 py-2 text-sm rounded-full font-semibold transition-colors duration-300
                               ${currentPage === index + 1 ? 'bg-cyan-500 text-white ' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {/* Employee Cards Display */}
              <div className="space-y-4">
                {currentItems.length > 0 ? (
                  currentItems.map((employee) => (
                    <div key={employee.id} className="relative">
                      <div
                        onClick={() => setSelectedRowId(employee.id === selectedRowId ? null : employee.id)}
                        className="flex items-center gap-4 mx-5 px-2 py-1  hover:bg-gray-700 transition-all duration-200 
                                   cursor-pointer rounded-2xl  border border-gray-300"
                      >
                        <div className="flex-shrink-0">
                          <img
                            className="rounded-full h-12 w-12 object-cover ring-2 ring-gray-700 "
                            src={employee?.photo_url || 'https://placehold.co/100x100/1f2937/e5e7eb?text=NP'}
                            alt={`${employee?.first_name} ${employee?.last_name} profile`}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = 'https://placehold.co/100x100/1f2937/e5e7eb?text=NP';
                            }}
                          />
                        </div>
                        <div className="flex-grow">
                          <div className="font-bold text-lg text-white ">
                            {employee?.first_name || 'N/A'} {employee?.last_name || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-200">
                            {employee?.departments?.name || 'N/A'}
                          </div>
                        </div>
                        <div className="text-white">
                          <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 transform transition-transform duration-300 ${selectedRowId === employee.id ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      
                      {selectedRowId === employee.id && (
                        <div
                          ref={dropdownRef}
                          className="absolute z-10 right-4 top-16 bg-[var(--currentColor)] backdrop-blur-md 
                                     ring-1 ring-cyan-500/30 border border-gray-300 rounded-xl p-2 w-48 
                                     flex flex-col animate-fade-in-down"
                        >
                          {employee.approved === 1 ? (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedRowId(null);
                                handleAction('view', employee);
                              }}
                              className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-yellow-300 transition duration-300 rounded-lg"
                            >
                              👁️ View
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRowId(null);
                                  handleAction('edit', employee);
                                }}
                                className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-blue-300 transition duration-300 rounded-t-lg"
                              >
                                ✏️ Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRowId(null);
                                  handleAction('view', employee);
                                }}
                                className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-yellow-300 transition duration-300"
                              >
                                👁️ View
                              </button>
                              <div className="border-t border-white/20 my-1" />
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedRowId(null);
                                  handleAction('deactivate', employee);
                                }}
                                className="text-left px-4 py-2 text-sm text-white hover:bg-white/20 hover:text-green-300 transition duration-300 rounded-b-lg"
                              >
                              ❌ Deactivate
                              </button>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-white/50 py-10">
                    <p className="text-lg">No employees found.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};
export default Employees;
